import { ActionType } from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import {
  CustomRuleGroupType,
  ManageConversationLabelsActionRule,
  RulesFormActionRule,
  SignalActionRule,
} from 'src/graphql/entities/behavior/behavior.types';
import { ConversationAssigneeGroupOption } from 'src/graphql/entities/conversation-assignee-group';
import { Macro } from './macro';

export enum MacrosFormFields {
  Name = 'name',
  Description = 'description',
  Visibility = 'public',
  AvailableFor = 'conversationAssigneeGroups',
  MessagePrivacy = 'messagePrivacy',
  SnippetSource = 'snippetSource',
  Template = 'template',
  AiPromptId = 'aiPromptId',
  AiPromptText = 'aiPromptText',
  Actions = 'actions',
  RecordFolderId = 'recordFolderId',
}

/**
 * Where a macro's text snippet comes from. Derived from the stored columns
 * rather than persisted on its own, so the source can never disagree with the
 * data: a macro carrying an AI prompt is an AI-prompt macro, everything else is
 * static text.
 */
export enum MacroSnippetSource {
  StaticText = 'staticText',
  AiPrompt = 'aiPrompt',
}

export enum MacroVisibility {
  Everyone = 'public',
  OnlyMe = 'private',
  SpecificGroups = 'groups',
}

export enum MacroMessagePrivacy {
  Private = 'private',
  Public = 'public',
}

export type MacroActionRule = Extract<
  RulesFormActionRule,
  CustomRuleGroupType | ManageConversationLabelsActionRule | SignalActionRule
> & {
  macroId: Macro['id'];
};
export type MacroActionType = Extract<
  ActionType,
  'data' | 'manageConversationLabels' | 'signal'
>;

type MacrosFormAction =
  | {
      id: string;
      actionType: ActionType.Data;
      actions: CustomRuleGroupType;
    }
  | {
      id: string;
      actionType: ActionType.ManageConversationLabels;
      actions: ManageConversationLabelsActionRule;
    }
  | {
      id: string;
      actionType: ActionType.Signal;
      actions: SignalActionRule;
    };

export type MacrosFormActions = MacrosFormAction[];

export interface MacrosFormModel {
  [MacrosFormFields.Name]: string;
  [MacrosFormFields.Description]?: string;
  [MacrosFormFields.SnippetSource]: MacroSnippetSource;
  [MacrosFormFields.Template]: string;
  [MacrosFormFields.AiPromptId]: string | null;
  [MacrosFormFields.AiPromptText]: string;
  [MacrosFormFields.Actions]: MacrosFormActions;
  [MacrosFormFields.MessagePrivacy]: MacroMessagePrivacy;
  [MacrosFormFields.Visibility]: MacroVisibility;
  [MacrosFormFields.AvailableFor]: ConversationAssigneeGroupOption[];
  [MacrosFormFields.RecordFolderId]?: MacroFolderOption | null;
}

export interface MacroFolderOption {
  id: string;
  label: string;
  value: string;
  partnerId: number;
}

/**
 * Payload written to the macro entity. Declared explicitly (rather than
 * inferred from the mapper) so the snippet branches stay mutually exclusive:
 * a static macro nulls both prompt columns, an AI macro empties the template.
 */
export interface MacroWriteRequest {
  partnerId: number;
  name: string;
  description?: string;
  template: string;
  aiPromptId: string | null;
  aiPromptText: string | null;
  actions: string;
  messagePrivacy: MacroMessagePrivacy;
  public: boolean;
}
